/** @jsx isolateComponent */
import { Children } from 'react';
import type { ComponentProps } from 'react';
import { css } from '@emotion/core';

import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { Colors, Typography } from '../../../theme';
import { TooltipWrapper } from '../../display/TooltipWrapper';
import { Avatar } from './Avatar';

export type Props = {
  /**
   * Array of `children` to render, typically `Avatar`s.
   * This prop is specifically typed to allow for `map`ing data
   * to `Element`s like `Avatar` and counting the rest.
   */
  children: JSX.Element[];
  /**
   * Controls the maximum number of `children` to render before
   * rendering a counter for the remaining children.
   */
  max?: number;
  /**
   * The `size` of the `Avatar`s that render within the group.
   */
  size?: Pick<ComponentProps<typeof Avatar>, 'size'>;
};

const Styles = labelStyles('AvatarGroup', {
  root: css`
    ${Typography.base};
    display: flex;
    align-items: center;
    user-select: none;
    width: max-content;

    &[data-size='40'] > * {
      margin-right: -10px;
    }
    &[data-size='25'] > * {
      margin-right: -5px;
    }
    &[data-size='18'] > * {
      margin-right: -3px;
    }
    &[data-size] > *:last-of-type {
      margin-right: 0;
    }
  `,
  count: css`
    --AvatarGroup__count-text-indent: 0px;
    position: relative;
    display: flex;
    align-items: center;
    text-align: center;
    color: ${Colors.gray};
    background-color: ${Colors.gray10};
    border: 1px solid #fff;
    border-radius: 100px;
    font-weight: 700;
    line-height: 1;

    &::before {
      content: '+';
      text-indent: var(--AvatarGroup__count-text-indent);
    }

    [data-size='40'] & {
      --AvatarGroup__count-text-indent: -3px;
      font-size: 14px;
      padding: 0 10px;
      height: 40px;
      min-width: 40px;
      border-width: 2px;
    }

    [data-size='25'] & {
      --AvatarGroup__count-text-indent: -2px;
      font-size: 11px;
      height: 25px;
      min-width: 25px;
      padding: 0 6px;
    }

    [data-size='18'] & {
      --AvatarGroup__count-text-indent: -1px;
      font-size: 8px;
      height: 18px;
      min-width: 18px;
      padding: 0 4px;
    }
  `,
});

const defaultProps = {
  max: 3,
};

/**
 * Displays a group of `Avatar` components.
 */
export const AvatarGroup = (_props: Props) => {
  const props = { ...defaultProps, ..._props };
  const childrenArray = Children.toArray(props.children).filter(Boolean);
  const slicedChildren = childrenArray.slice(0, props.max);
  const remainingChildren = childrenArray.length - slicedChildren.length;
  return (
    <div css={Styles.root} data-size={props.size}>
      {slicedChildren}
      {remainingChildren > 0 && (
        <TooltipWrapper
          content={`Plus ${remainingChildren} others`}
          position="southeast"
        >
          {() => (
            <div
              aria-label={`Plus ${remainingChildren} others`}
              css={Styles.count}
            >
              {Math.min(remainingChildren, 999)}
            </div>
          )}
        </TooltipWrapper>
      )}
    </div>
  );
};
